<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import type { AddToNextOrderAction } from '@/lib/addToNextOrderAction'
import { countup } from '@/lib/countup'
import { extractShopifyId } from '@/lib/gid'
import { track } from '@/lib/track'
import type { Product } from '@/services/product'
import {
  getFreeGiftForProduct,
  type CollectionFreeGift,
} from '@/services/storefront'

import type { AuthenticatedEntity } from '@/stores/auth'
import { useCartStore } from '@/stores/cart'
import { useCompletedActionStore } from '@/stores/completedAction'
import { useLastOrderStore } from '@/stores/lastOrder'
import { AsyncOperationState } from '@/stores/lib/AsyncOperationState'
import { useNextOrderStore } from '@/stores/nextOrder'
import { useOnetimeStore } from '@/stores/onetime'
import { useProductImageStore } from '@/stores/productImage'
import { useProductSearchStore } from '@/stores/productSearch'
import { useRecommendationStore } from '@/stores/recommendation'
import { useShopStore } from '@/stores/shop'
import { useSubscriptionStore } from '@/stores/subscription'
import { useSubscriptionCustomerStore } from '@/stores/subscriptionCustomer'
import { useWasteMetricsStore } from '@/stores/wasteMetrics'
import { useNextOrderDelay } from '@/views/lib/useNextOrderDelay'

import IconArrowRight from '@/assets/icons/arrow-right.svg?component'
import AddToNextOrderUpsell from '@/components/AddToNextOrderUpsell.vue'
import AddToOrderModal from '@/components/AddToOrderModal.vue'
import Button from '@/components/Button.vue'
import CategoryList from '@/components/CategoryList.vue'
import ChargeErrorBanner from '@/components/ChargeErrorBanner.vue'
import CurrentDelivery from '@/components/CurrentDelivery.vue'
import DatePicker from '@/components/DatePicker.vue'
import ErrorMessage from '@/components/ErrorMessage.vue'
import FeaturedProduct from '@/components/FeaturedProduct.vue'
import HorizontalProductList from '@/components/HorizontalProductList.vue'
import DefaultLayout from '@/components/layout/DefaultLayout.vue'
import SimpleLayout from '@/components/layout/SimpleLayout.vue'
import Loading from '@/components/Loading.vue'
import LoadingScreen from '@/components/LoadingScreen.vue'
import MessageCta from '@/components/MessageCta.vue'
import NextOrderConfirmation from '@/components/NextOrderConfirmation.vue'
import NextOrderConfirmationModal from '@/components/NextOrderConfirmationModal.vue'
import NextOrderPreview from '@/components/NextOrderPreview.vue'
import Salutation from '@/components/Salutation.vue'

const router = useRouter()
const props = defineProps<{ authedUser: AuthenticatedEntity }>()
const subscriptionCustomerStore = useSubscriptionCustomerStore()
const nextOrderStore = useNextOrderStore()
const productImageStore = useProductImageStore()
const cartStore = useCartStore()
const lastOrderStore = useLastOrderStore()
const recommendationStore = useRecommendationStore()
const wasteMetricsStore = useWasteMetricsStore()
const shopStore = useShopStore()
const productSearchStore = useProductSearchStore()
const subscriptionStore = useSubscriptionStore()
const onetimeStore = useOnetimeStore()
const completedActionStore = useCompletedActionStore()
const featuredAddState = ref(AsyncOperationState.IDLE)
const featuredProductToAdd = ref<Product | null>(null)
const featuredFreeGift = ref<CollectionFreeGift | null>(null)

if (props.authedUser.rechargeId) {
  subscriptionCustomerStore.init(props.authedUser.rechargeId)
  nextOrderStore.init(props.authedUser.rechargeId)
  subscriptionCustomerStore.init(props.authedUser.rechargeId)
  recommendationStore.init(
    props.authedUser.shopifyId,
    props.authedUser.rechargeId,
    'new',
    [],
  )
  // Always refetch so a stale in-memory list cannot keep the tag-gated
  // featured section visible after the API starts returning [].
  recommendationStore.load(
    props.authedUser.shopifyId,
    props.authedUser.rechargeId,
    'featured',
    [],
  )
  subscriptionStore.init(props.authedUser.rechargeId)
}

lastOrderStore.init(props.authedUser.shopifyId)
wasteMetricsStore.initCustomerWasteMetrics(props.authedUser.shopifyId)
shopStore.init()

const { showDatePicker, changeDate, confirmChangeDate } = useNextOrderDelay(
  props.authedUser.rechargeId,
)

const { ref: customerPlasticWasteSavedCount, start: startWasteMetricsCountUp } =
  countup(0)

watch(
  () => wasteMetricsStore.customerWasteMetrics,
  (newValue) => {
    if (newValue && newValue.weight) {
      startWasteMetricsCountUp(newValue.weight / 1000, 1500)
    }
  },
  { immediate: true },
)

const subscriptionsNotInNextOrder = computed(() => {
  const nextOrder = nextOrderStore.order
  if (!nextOrder) return []
  return subscriptionStore.subscriptions
    .filter((s) => s.status.toLowerCase() === 'active')
    .filter((s) => nextOrder.scheduled_at !== s.next_charge_scheduled_at)
})

const featuredProduct = computed(() => {
  const nextOrderProductIds = new Set(
    (nextOrderStore.order?.line_items ?? []).map(
      (item) => item.external_product_id.ecommerce,
    ),
  )
  return recommendationStore.featured.items.find(
    (product) => !nextOrderProductIds.has(extractShopifyId(product.id)),
  )
})

// Featured upsell is tag-gated in the API (`test:upsell_recommendation`).
// Require DONE so a previous in-memory list cannot flash the section
// while a refetch is in progress.
const showFeaturedProduct = computed(
  () =>
    recommendationStore.featured.loadState === AsyncOperationState.DONE &&
    featuredProduct.value != null,
)

const featuredIntroText = computed(() => {
  const product = featuredProduct.value
  if (!product) return null
  return (
    recommendationStore.featured.introTexts[extractShopifyId(product.id)] ??
    null
  )
})

watch(
  () => featuredProduct.value?.id,
  async (productId) => {
    featuredFreeGift.value = null
    if (!productId) return
    try {
      const gift = await getFreeGiftForProduct(productId)
      if (featuredProduct.value?.id === productId) {
        featuredFreeGift.value = gift
      }
    } catch {
      if (featuredProduct.value?.id === productId) {
        featuredFreeGift.value = null
      }
    }
  },
  { immediate: true },
)

const onAddToNextOrder = async (subscriptionId: number) => {
  await subscriptionStore.addSubscriptionToNextOrder(
    props.authedUser.rechargeId,
    subscriptionId,
  )
  if (subscriptionStore.workState === AsyncOperationState.DONE) {
    nextOrderStore.init(props.authedUser.rechargeId)
    const subscription = subscriptionStore.subscriptions.find(
      (s) => s.id === subscriptionId,
    )
    if (subscription) {
      completedActionStore.setCompletedAction({
        type: 'subscription_updated',
        data: {
          imageSrc:
            productImageStore.productImages[
              `${subscription.external_product_id.ecommerce}:${subscription.external_variant_id.ecommerce}`
            ]?.[0]?.small ?? undefined,
          title: subscription.product_title,
          scheduledAt: subscription.next_charge_scheduled_at!,
          quantity: subscription.quantity,
          frequency: subscription.charge_interval_frequency,
        },
        autoDismiss: true,
      })
      completedActionStore.sendToBackground()
    }
  } else if (subscriptionStore.workState === AsyncOperationState.ERRORED) {
    console.error(
      'Error adding subscription to next order',
      subscriptionStore.workState,
    )
  }
}

const featuredExistingSubscriptions = computed(() => {
  const product = featuredProductToAdd.value
  if (!product) return []
  const productId = extractShopifyId(product.id)
  return subscriptionStore.subscriptions
    .filter((s) => s.external_product_id.ecommerce === productId)
    .filter((s) => s.status === 'active')
})

const onAddFeaturedProductToNextOrder = (product: Product) => {
  if (featuredAddState.value === AsyncOperationState.IN_PROGRESS) return
  featuredProductToAdd.value = product
}

const onConfirmFeaturedAddToNextOrder = (action: AddToNextOrderAction) => {
  const product = featuredProductToAdd.value
  if (!product) return
  addFeaturedProductToNextOrder(product, action)
}

const addFeaturedProductToNextOrder = async (
  product: Product,
  action: AddToNextOrderAction,
) => {
  if (featuredAddState.value === AsyncOperationState.IN_PROGRESS) return

  featuredAddState.value = AsyncOperationState.IN_PROGRESS
  const sellingPlan =
    action.type === 'add-new-subscription' ? action.selectedSellingPlan : null
  const freeGift = featuredFreeGift.value

  if (action.type === 'move-existing-subscription') {
    await subscriptionStore.addSubscriptionToNextOrder(
      props.authedUser.rechargeId,
      action.subscriptionId,
    )
    featuredAddState.value = subscriptionStore.workState
  } else if (action.type === 'add-new-subscription') {
    await subscriptionStore.addNewSubscriptionToNextOrder(
      props.authedUser.rechargeId,
      extractShopifyId(product.id),
      extractShopifyId(action.variantId),
      action.quantity,
      extractShopifyId(action.selectedSellingPlan.id),
      recommendationStore.featured.offerPercents[extractShopifyId(product.id)],
    )
    featuredAddState.value = subscriptionStore.workState
  } else {
    await onetimeStore.addToNextOrder(
      props.authedUser.rechargeId,
      extractShopifyId(product.id),
      extractShopifyId(action.variantId),
      action.quantity,
    )
    featuredAddState.value = onetimeStore.workState
  }

  if (
    featuredAddState.value === AsyncOperationState.DONE &&
    freeGift &&
    action.type !== 'move-existing-subscription'
  ) {
    const existingGift = (nextOrderStore.order?.line_items ?? []).find(
      (item) =>
        item.external_variant_id.ecommerce ===
          extractShopifyId(freeGift.variantId) ||
        item.external_product_id.ecommerce ===
          extractShopifyId(freeGift.productId),
    )
    const giftAlreadyFree = existingGift?.unit_price === '0.00'
    if (!giftAlreadyFree) {
      if (existingGift?.purchase_item_type === 'onetime') {
        await onetimeStore.cancelOnetime(
          props.authedUser.rechargeId,
          existingGift.purchase_item_id,
        )
      }
      if (onetimeStore.workState !== AsyncOperationState.ERRORED) {
        await onetimeStore.addFreeGiftToNextOrder(
          props.authedUser.rechargeId,
          extractShopifyId(product.id),
          extractShopifyId(freeGift.variantId),
        )
      }
      if (onetimeStore.workState === AsyncOperationState.ERRORED) {
        track('featured_free_gift_add_error', {
          product_id: product.id,
          gift_product_id: freeGift.productId,
          gift_variant_id: freeGift.variantId,
          section: 'featured',
        })
      }
    }
  }

  if (featuredAddState.value === AsyncOperationState.DONE) {
    featuredProductToAdd.value = null
    completedActionStore.setCompletedAction({
      type: 'next_order_product_added',
      data: {
        productId: product.id,
        variantId: action.variantId,
        quantity: action.quantity,
        sellingPlan,
        product,
      },
      autoDismiss: true,
    })
    completedActionStore.sendToBackground()
    track(
      action.type === 'add-onetime'
        ? 'onetime_add_to_next_order'
        : 'subscription_add_to_next_order',
      {
        product_id: product.id,
        variant_id: action.variantId,
        quantity: action.quantity,
        section: 'featured',
      },
    )
    await nextOrderStore.init(props.authedUser.rechargeId)
  } else if (featuredAddState.value === AsyncOperationState.ERRORED) {
    track(
      action.type === 'add-onetime'
        ? 'onetime_add_to_next_order_error'
        : 'subscription_add_to_next_order_error',
      {
        product_id: product.id,
        variant_id: action.variantId,
        quantity: action.quantity,
        section: 'featured',
      },
    )
  }
}

onMounted(() => nextOrderStore.resetWorkState())
const onSearch = (query: string) => {
  productSearchStore.setQuery(query)
  router.push(`/shop`)
}
</script>

<template>
  <SimpleLayout
    v-if="nextOrderStore.workState === AsyncOperationState.IN_PROGRESS"
    :has-header="false"
  >
    <LoadingScreen :messages="['Rescheduling your order…']" />
  </SimpleLayout>
  <DefaultLayout
    v-else-if="
      nextOrderStore.workState === AsyncOperationState.DONE &&
      nextOrderStore.order
    "
    location="home"
    :has-header="true"
    :cart-count="nextOrderStore.order.line_items.length"
    :is-subscriber="!!authedUser.rechargeId"
    @back="router.back()"
    @go-to-cart="router.push('/cart')"
    @go-to-home="router.push('/')"
    @go-to-upcoming-orders="router.push('/upcoming-orders')"
    @go-to-your-products="router.push('/product-subscriptions')"
  >
    <div class="NextOrder__ConfirmationContainer Container Stack">
      <NextOrderConfirmation
        :order="nextOrderStore.order"
        :images-by-product-id="productImageStore.productImages"
        status="rescheduled"
        @view-order="router.push('/next-order')"
      ></NextOrderConfirmation>
    </div>
  </DefaultLayout>
  <DefaultLayout
    v-else
    title="Home"
    :cart-count="cartStore.items.length"
    :is-subscriber="!!authedUser.rechargeId"
    location="home"
    @back="router.back()"
    @go-to-cart="router.push('/cart')"
    @go-to-home="router.push('/')"
    @go-to-upcoming-orders="router.push('/upcoming-orders')"
    @go-to-your-products="router.push('/product-subscriptions')"
  >
    <template
      v-if="
        !authedUser.rechargeId ||
        (!nextOrderStore.order &&
          nextOrderStore.loadState !== AsyncOperationState.IN_PROGRESS)
      "
    >
      <div class="HomeView__Container Container Stack">
        <div class="HomeView__MessagesContainer">
          <MessageCta
            title="Welcome to Bower"
            class="HomeView__MessageCta"
            hide-arrow
          >
            <template #body
              >If you had any subscriptions, this is where you'd come to manage
              them.</template
            >
          </MessageCta>
          <div class="HomeView__EcoStats">
            <div class="HomeView__EcoStatsImage"></div>
            <p class="HomeView__EcoStatsText">
              Since you've been a Bower customer, you've saved
              <strong
                >{{
                  Math.round((customerPlasticWasteSavedCount * 1000) / 14)
                }}
                plastic</strong
              >
              bottles worth of waste!
            </p>
          </div>
        </div>
        <div class="HomeView__AccountLinks">
          <h6 class="HomeView__AccountLinksHeader">Your orders</h6>
          <div class="HomeView__AccountLinksList">
            <Button
              :link="true"
              href="/previous-orders"
              @click.prevent.stop="router.push('/previous-orders')"
            >
              View your previous orders
              <template #iconAfter><IconArrowRight width="16" /></template>
            </Button>
            <Button :link="true" href="https://bowercollective.com">
              Want to place a new order? Shop now
              <template #iconAfter><IconArrowRight width="16" /></template>
            </Button>
          </div>
        </div>
      </div>
    </template>
    <template
      v-else-if="subscriptionCustomerStore.customer && nextOrderStore.order"
    >
      <div class="HomeView__Container Container Stack Stack--small">
        <Salutation
          :copy="`Welcome back, ${subscriptionCustomerStore.customer.first_name}!`"
        />

        <CurrentDelivery
          v-if="lastOrderStore.isRecent && lastOrderStore.order"
          :order="lastOrderStore.order"
        />
        <ChargeErrorBanner
          v-if="nextOrderStore.order.status !== 'queued'"
          :charge="nextOrderStore.order"
        />
        <NextOrderPreview
          :show-plastic-waste-savings="true"
          :order="nextOrderStore.order"
          :images-by-product-id="productImageStore.productImages"
          @edit="router.push('/next-order')"
          @change-date="changeDate"
        ></NextOrderPreview>
        <AddToNextOrderUpsell
          v-if="subscriptionsNotInNextOrder.length > 0"
          :minimisable="true"
          :subscriptions="subscriptionsNotInNextOrder"
          :next-order-scheduled-at="nextOrderStore.order?.scheduled_at ?? null"
          :work-state="subscriptionStore.workState"
          :product-images="productImageStore.productImages"
          @add-to-next-order="onAddToNextOrder"
        />
        <FeaturedProduct
          v-if="showFeaturedProduct"
          title="Recommended for you"
          :product="featuredProduct"
          :customer-name="subscriptionCustomerStore.customer.first_name"
          :reasons="recommendationStore.featured.reasons"
          :offer-percents="recommendationStore.featured.offerPercents"
          :intro-text="featuredIntroText"
          :load-state="recommendationStore.featured.loadState"
          :work-state="featuredAddState"
          section="featured"
          @add-to-next-order="onAddFeaturedProductToNextOrder"
        ></FeaturedProduct>
        <CategoryList
          :categories="shopStore.categories"
          :load-state="shopStore.menuLoadState"
          @search="onSearch"
          @select-category="router.push(`/shop/${$event}`)"
        />
        <div class="HomeView__MessagesContainer">
          <div class="HomeView__EcoStats">
            <div class="HomeView__EcoStatsImage"></div>
            <p class="HomeView__EcoStatsText">
              Since you've been a Bower customer, you've saved
              <strong
                >{{
                  Math.round((customerPlasticWasteSavedCount * 1000) / 14)
                }}
                plastic</strong
              >
              bottles worth of waste!
            </p>
          </div>
        </div>

        <HorizontalProductList
          :products="recommendationStore.new.items"
          title="Try something new…"
          :load-state="recommendationStore.new.loadState"
          section="try_something_new"
        ></HorizontalProductList>

        <div class="HomeView__AccountLinks">
          <h6 class="HomeView__AccountLinksHeader">
            Need to check your details?
          </h6>
          <div class="HomeView__AccountLinksList">
            <Button :link="true" @click="router.push('/account-details')">
              Account details
              <template #iconAfter><IconArrowRight width="16" /></template>
            </Button>
            <Button :link="true" @click="router.push('/addresses')">
              Saved addresses
              <template #iconAfter><IconArrowRight width="16" /></template>
            </Button>
            <Button :link="true" @click="router.push('/payment-methods')">
              Saved payment methods
              <template #iconAfter><IconArrowRight width="16" /></template>
            </Button>
            <Button
              :link="true"
              href="/previous-orders"
              @click.prevent.stop="router.push('/previous-orders')"
            >
              Previous orders
              <template #iconAfter><IconArrowRight width="16" /></template>
            </Button>
          </div>
        </div>

        <div class="HomeView__AccountLinks">
          <h6 class="HomeView__AccountLinksHeader">
            Need help with your account?
          </h6>
          <div class="HomeView__AccountLinksList">
            <Button
              :link="true"
              href="https://bowercollective.com/pages/help-and-contact"
            >
              Read our FAQs
              <template #iconAfter><IconArrowRight width="16" /></template>
            </Button>
            <Button :link="true" href="mailto:hello@bowercollective.com">
              Email us
              <template #iconAfter><IconArrowRight width="16" /></template>
            </Button>
          </div>
        </div>
      </div>
    </template>
    <template
      v-else-if="
        subscriptionCustomerStore.loadState ===
          AsyncOperationState.IN_PROGRESS ||
        nextOrderStore.loadState === AsyncOperationState.IN_PROGRESS
      "
    >
      <div class="HomeView__Loading">
        <Loading />
      </div>
    </template>
    <template v-else>
      <ErrorMessage>
        <template #title>Error loading your details</template>
        <template #default>
          <p>There was an error loading your details.</p>
        </template>
      </ErrorMessage>
    </template>
    <Teleport to="#modal-container">
      <AddToOrderModal
        v-if="featuredProductToAdd"
        :selected-product="featuredProductToAdd"
        :existing-subscriptions="featuredExistingSubscriptions"
        :add-to-next-order-state="featuredAddState"
        :next-order-scheduled-at="nextOrderStore.order?.scheduled_at ?? null"
        :offer-percent="
          recommendationStore.featured.offerPercents[
            extractShopifyId(featuredProductToAdd.id)
          ]
        "
        :free-gift="featuredFreeGift"
        :intro-text="featuredIntroText"
        @add-to-next-order="onConfirmFeaturedAddToNextOrder"
        @close="featuredProductToAdd = null"
      />
    </Teleport>
    <Teleport to="#modal-container">
      <NextOrderConfirmationModal
        v-if="
          router.currentRoute.value.query.status ===
            'next-order-confirmation' && nextOrderStore.order
        "
        :order="nextOrderStore.order"
        :images-by-product-id="productImageStore.productImages"
        @done="router.push('/')"
      />
    </Teleport>
    <Teleport to="#modal-container">
      <transition name="DatePicker" appear>
        <div
          v-if="showDatePicker && nextOrderStore.order"
          class="NextOrder__DatePicker__Outer"
        >
          <DatePicker
            back-link-text="Back"
            title="When would you like us to process your order?"
            description="Your order will arrive within 2-4 working days of processing"
            button-text="Continue"
            :initial-selected-date="new Date(nextOrderStore.order.scheduled_at)"
            @close="showDatePicker = false"
            @confirm="confirmChangeDate"
          ></DatePicker>
        </div>
      </transition>
    </Teleport>
  </DefaultLayout>
</template>

<style>
.HomeView__Loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}

.HomeView__Container {
  padding: 0 20px;
  padding-bottom: 40px;
}

.HomeView__MessagesContainer {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.HomeView__EcoStats {
  background-color: var(--color-white);
  color: var(--color-forest-100);
  padding: 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  position: relative;
}

.HomeView__EcoStatsText {
  color: var(--color-forest-100);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
  padding-left: 50px;
  margin: 0;

  @media (--mq-tablet-and-up) {
    padding-left: 80px;
  }
}

.HomeView__EcoStatsImage {
  min-width: 64px;
  min-height: 73px;
  background-size: 64px 73px;
  background-image: url('@/assets/images/plastic-bottles@2x.png');
  transform: translate3d(-25px, 0, 0);
  position: absolute;

  @media (--mq-tablet-and-up) {
    transform: translate3d(0, 0, 0);
  }
}

.HomeView__AccountLinks {
  padding: 20px;
  border-radius: 8px;
  border-top: 1px solid var(--color-white);
  background: var(--color-white);
}

.HomeView__AccountLinksHeader {
  margin: 0;
  color: var(--color-forest-100);
  font-family: var(--font-family-domaine);
  font-size: 21px;
  font-style: normal;
  font-weight: 400;
}

.HomeView__AccountLinksList {
  width: 100%;
  max-width: 600px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  margin-top: 12px;

  @media (--mq-tablet-and-up) {
    grid-template-columns: 1fr 1fr;
  }
}

.NextOrder__DatePicker__Outer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 6;
  background-color: var(--color-lichen-25);
  overflow-y: auto;
  overscroll-behavior-y: none;
}

.DatePicker-enter-from,
.DatePicker-leave-to {
  opacity: 0;
  transform: translateX(-50%);
}

.DatePicker-enter-to,
.DatePicker-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.DatePicker-enter-active,
.DatePicker-leave-active {
  transition:
    opacity 0.2s ease-in,
    transform 0.18s ease-in;
}

@media (--mq-tablet-and-up) {
  .NextOrder__DatePicker__Outer {
    position: fixed;
    background-color: transparent;
    z-index: 5;
    width: 480px;
    height: auto;
    inset: 50% auto auto 50%;
    transform: translate(-50%, -50%);
    max-height: 100vh;
    overflow: hidden scroll;
    overscroll-behavior: none;
  }

  .DatePicker-enter-from,
  .DatePicker-leave-to {
    opacity: 0;
    transform: translate(-50%, -50%);
  }

  .DatePicker-enter-to,
  .DatePicker-leave-from {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
}
</style>
